import React, { Fragment, useRef } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { NativeTypes } from 'react-dnd-html5-backend'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import useImgResize from './hooks/useImgResize'
import useImgLink from './hooks/useImgLink'

import { ReactComponent as AddImage } from './icons/addImage.svg'
import { ReactComponent as Delete } from '../shared/icons/delete.svg'
import { ReactComponent as DownloadImage } from './icons/download.svg'

import FileDropTarget from './FileDropTarget'
import { ImageWithoutLink } from './Image'

import style from './ImageUploader.module.scss'

export const UploaderComponent = ({ elementMaxWidth = 240, label, isRound, isUploading, handleUpload, handleDelete, src: propsSrc, height, width, dimensions, fileToString, imgKey, imgUrlRef, canRemove = true, canDownload = true, allowedFileTypes = ['image/jpg', 'image/png', 'image/jpeg'] }) => {
	const inputRef = useRef({})
	const [t] = useTranslation()
	const resizer = useImgResize({ width, height })
	const { src, isLoading } = useImgLink({ imgKey, isUploading, imgUrlRef, src: propsSrc })

	const toDataUri = blob => new Promise((resolve, reject) => {
		const reader = new FileReader()
		reader.onloadend = () => resolve(reader.result)
		reader.onerror = reject
		reader.readAsDataURL(blob)
	})
	const handleInputChange = async e => {
		let file = await resizer(e.target.files[0])
		if(fileToString) {
			file = await toDataUri(file)
		}
		handleUpload(file)
		inputRef.current.value = ''
	}
	const openFileDialog = () => {
		inputRef.current.click()
	}
	const onDeleteImg = (...args) => {
		inputRef.current.value = ''
		handleDelete(...args)
	}
	const handleFileDrop = async file => {
		if(allowedFileTypes.includes(file.type)) {
			let fileRes = await resizer(file)
			if(fileToString) {
				fileRes = await toDataUri(fileRes)
			}
			handleUpload(fileRes)
		}
	}

	return(
		<div className={classNames(style.root, { [style.userphoto]: isRound })}>
			{label ? <p>{label}</p> : null }
			{dimensions && dimensions}
			<FileDropTarget accepts={[NativeTypes.FILE]} onDrop={handleFileDrop} render={({ isOver, canDrop, isActive }) => {
				let out
				if(isUploading) {
					out = <div><div className={style.loader}/></div>
				} else if(isActive) {
					out = <DropScreen/>
				} else {
					out = (
						src ?
							(<ImageWithOverlay
								src={src}
								isLoading={isLoading}
								onRemove={onDeleteImg}
								onAdd={openFileDialog}
								canRemove={canRemove}
								canDownload={canDownload}
								elementMaxWidth={elementMaxWidth}
								height={height}
								width={width}
								t={t}
							/>) :
							<AddScreen onAdd={openFileDialog} showResolution={!isRound} height={height} width={width} t={t} />
					)
				}
				return(
					<div
						className={style['overlay-container']}
						style={{
							paddingTop: `${height / width * elementMaxWidth}px`, //aspect ratio
							width: elementMaxWidth
						}}
					>
						<input
							ref={inputRef}
							type='file'
							accept={allowedFileTypes.join(',')}
							label='File'
							onChange={handleInputChange}
							style={{ display: 'none' }}
						/>
						{out}
					</div>
				)
			}}/>
		</div>
	)
}


const ImageWithOverlay = ({ onRemove, onDownload, onAdd, elementMaxWidth, height, width, t, canRemove, canDownload, ...props }) => (
	<Fragment>
		<ImageWithoutLink
			{...props}
			style={{
				maxHeight: `${height / width * elementMaxWidth}px`, //aspect ratio
				maxWidth: elementMaxWidth
			}}
		/>
		<div className={classNames(style.overlay)}>
			{!canRemove ?
				<div onClick={onAdd} className={style.btnContainer}>
					<AddImage />
					<p>{t('common.strings.select_image').toUpperCase()}</p>
				</div> :
				<div onClick={onRemove} className={style.btnContainer}>
					<Delete/>
					<p>{t('common.strings.clear_image').toUpperCase()}</p>
				</div>
			}
			{canDownload &&
				<a href={props.src} download target='_blank' rel='noopener noreferrer' className={classNames(style.btnContainer, style.active)}>
					<DownloadImage/>
					<p>{t('common.strings.download_image').toUpperCase()}</p>
				</a>
			}
		</div>
	</Fragment>
)

const AddScreen = ({ onAdd, showResolution, height, width, t }) => (
	<div className={style.placeholder}>
		<div onClick={onAdd} className={style.btnContainer}>
			<AddImage />
			<p>{t('common.strings.select_image').toUpperCase()}</p>
		</div>
		{showResolution && width && height && <span>{t('common.fields.image')} {width}x{height} Pixel</span>}
	</div>
)

const DropScreen = () => (
	<div className={style.placeholder}>
		<AddImage style={{
			width: 50,
			height: 50
		}}/>
	</div>
)

export const StyledContainerNoDimensions = styled.div`
	div[class*=overlay-container] {
		width: auto !important;
		height: ${({ hasImg }) => hasImg ? 'auto !important;' : ';'}
		padding-top: 0 !important;
	}
	div[class*=placeholder] {
		position: static !important;
		transform: none !important;
	}
	img {
		width: auto;
		height: auto;
		position: static !important;
		transform: none !important;
	}
	& > div > div {
		display: block !important;
	}
`

UploaderComponent.propTypes = {
	width: PropTypes.number.isRequired, // Needed output width of image
	height: PropTypes.number.isRequired, // Needed output height of image
	label: PropTypes.string,
	isRound: PropTypes.bool,
	isUploading: PropTypes.bool,
	handleUpload: PropTypes.func.isRequired,
	handleDelete: PropTypes.func.isRequired,
	src: PropTypes.string,
	dimensions: PropTypes.string,
	fileToString: PropTypes.bool,
	canRemove: PropTypes.bool,
	canDownload: PropTypes.bool,
	allowedFileTypes: PropTypes.array
}

export default UploaderComponent
